import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { getApiUrl } from '../../utils/authUtils';
import {
  CreditCard,
  TrendingUp,
  DollarSign,
  AlertCircle,
  Building,
  XCircle,
  ArrowRight,
  Info,
} from 'lucide-react';

const API = getApiUrl();

/**
 * Circular Progress Ring for Utilization
 */
const UtilizationRing = ({ percentage, color, size = 120, strokeWidth = 10, isDark = true }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (percentage / 100) * circumference;
  
  // Theme-aware colors for better visibility
  const trackColor = isDark 
    ? 'rgba(167, 243, 208, 0.18)' // Visible mint-green track in dark mode
    : 'rgba(6, 78, 59, 0.14)';   // Visible emerald-gray track in light mode
  
  const percentageTextColor = isDark ? '#f8fafc' : '#0f172a';  // High contrast
  const labelTextColor = isDark ? '#a7f3d0' : '#64748b';        // Readable muted color
  
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="transform -rotate-90">
        {/* Background track - visible in both themes */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={trackColor}
          strokeWidth={strokeWidth}
        />
        {/* Active arc */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          className="transition-all duration-1000 ease-out"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span 
          className="text-2xl font-extrabold" 
          style={{ color: percentageTextColor }}
        >
          {percentage}%
        </span>
        <span 
          className="text-xs font-medium" 
          style={{ color: labelTextColor }}
        >
          Used
        </span>
      </div>
    </div>
  );
};

/**
 * Skeleton loader for cards
 */
const CardSkeleton = ({ rows = 4 }) => (
  <div className="animate-pulse">
    <div className="h-5 w-32 bg-white/10 rounded mb-4" />
    {Array.from({ length: rows }).map((_, i) => (
      <div key={i} className="flex justify-between items-center py-2">
        <div className="h-4 w-24 bg-white/10 rounded" />
        <div className="h-4 w-16 bg-white/10 rounded" />
      </div>
    ))}
  </div>
);

/**
 * Glass Card Component matching dashboard style - Theme-aware
 */
const GlassCard = ({ children, className = '', onClick, clickable = false, theme }) => {
  // Default to dark mode styling if no theme provided
  const isDark = theme?.isDark !== false;
  
  return (
    <div
      onClick={onClick}
      className={`rounded-2xl p-5 ${className} ${clickable ? 'cursor-pointer transition-all duration-200 hover:scale-[1.02] hover:shadow-lg' : ''}`}
      style={{
        background: isDark ? 'rgba(15,45,35,0.75)' : 'rgba(255,255,255,0.88)',
        backdropFilter: 'blur(18px) saturate(140%)',
        WebkitBackdropFilter: 'blur(18px) saturate(140%)',
        border: isDark ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(15,172,108,0.12)',
        boxShadow: isDark 
          ? '0 12px 40px rgba(0,0,0,0.25), inset 0 1px 0 rgba(142,218,182,0.25)'
          : '0 12px 40px rgba(16,24,40,0.08), inset 0 1px 0 rgba(255,255,255,0.5)',
      }}
    >
      {children}
    </div>
  );
};

/**
 * Credit Profile Snapshot Component
 * Displays Account Summary, Credit Usage, and Debt Summary
 * No header or "View Full Report Analysis" link per requirements
 * 
 * @param {Object} theme - Theme object from dashboard
 * @param {string} reportId - Optional report_id to load specific historical report data
 */
const CreditProfileSnapshot = ({ theme, reportId }) => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Theme-aware text colors
  const isDark = theme?.isDark !== false;
  const textPrimary = isDark ? '#FFFFFF' : '#111827';
  const textMuted = isDark ? '#9CA3AF' : '#6B7280';
  const textSecondary = isDark ? '#D1D5DB' : '#374151';
  const accentGreen = isDark ? '#8FE0B7' : '#0D9F64';

  useEffect(() => {
    const fetchSnapshot = async () => {
      if (!token) return;
      
      setLoading(true);
      setError(null);
      
      try {
        // Build URL with optional report_id parameter
        let url = `${API}/dashboard/credit-snapshot`;
        if (reportId) {
          url += `?report_id=${reportId}`;
        }
        
        const response = await axios.get(url, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSnapshot(response.data);
      } catch (err) {
        console.error('Failed to fetch credit snapshot:', err);
        setError(err.response?.data?.detail || 'Failed to load credit profile');
      } finally {
        setLoading(false);
      }
    };

    fetchSnapshot();
  }, [token, reportId]);

  // Format currency - only show $0 for verified zero, otherwise show "Not available"
  const formatCurrency = (value, isVerifiedZero = false) => {
    if (value === null || value === undefined) return 'Not available';
    if (value === 0 && !isVerifiedZero) return '$0';
    if (isNaN(value)) return 'Not available';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  // Handle Overall Credit Usage card click - navigate to Credit Builder Tradelines
  const handleUtilizationCardClick = () => {
    navigate('/credit-builder/tradelines');
  };

  // If still loading, show skeletons (no header)
  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <GlassCard theme={theme}><CardSkeleton rows={7} /></GlassCard>
        <GlassCard theme={theme}><CardSkeleton rows={4} /></GlassCard>
        <GlassCard theme={theme}><CardSkeleton rows={6} /></GlassCard>
      </div>
    );
  }

  // If error, show error state (no header)
  if (error) {
    return (
      <GlassCard theme={theme}>
        <div className="flex items-center gap-3 text-red-400">
          <AlertCircle size={20} />
          <p className="text-sm">{error}</p>
        </div>
      </GlassCard>
    );
  }

  // If no data, show empty state (no header)
  if (!snapshot?.has_data) {
    return (
      <GlassCard theme={theme}>
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4" style={{ background: 'rgba(15,172,108,0.2)' }}>
            <CreditCard size={32} style={{ color: accentGreen }} />
          </div>
          <h3 className="text-lg font-semibold mb-2" style={{ color: textPrimary }}>No Credit Report Imported</h3>
          <p className="text-sm mb-4 max-w-md" style={{ color: textMuted }}>
            {snapshot?.message || "Import your credit report to see your credit profile snapshot."}
          </p>
          <Link
            to="/credit-report/import"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white"
            style={{ background: '#0FAC6C' }}
          >
            Import Credit Report
            <ArrowRight size={16} />
          </Link>
        </div>
      </GlassCard>
    );
  }

  const { account_summary, utilization_summary, debt_summary, data_completeness } = snapshot;

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="credit-profile-snapshot">
      {/* Card 1: Account Summary */}
      <GlassCard theme={theme}>
        <div className="flex items-center gap-2 mb-4">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(15,172,108,0.2)' }}>
            <Building size={18} style={{ color: accentGreen }} />
          </div>
          <h3 className="font-semibold" style={{ color: textPrimary }}>Account Summary</h3>
        </div>
        
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <span className="text-sm" style={{ color: textMuted }}>Open accounts</span>
            <span className="font-medium" style={{ color: textPrimary }}>{account_summary?.open_accounts ?? 0}</span>
          </div>
          
          {/* Self-reported accounts - only show if supported, otherwise hide or show "Not available" */}
          {account_summary?.self_reported_accounts !== null && (
            <div className="flex justify-between items-center">
              <span className="text-sm" style={{ color: textMuted }}>Self-reported accounts</span>
              <span className="font-medium" style={{ color: textPrimary }}>{account_summary.self_reported_accounts}</span>
            </div>
          )}
          
          <div className="flex justify-between items-center">
            <span className="text-sm" style={{ color: textMuted }}>Accounts with late history</span>
            <span className="font-medium" style={{ color: account_summary?.accounts_with_late_history > 0 ? '#FBBF24' : textPrimary }}>
              {account_summary?.accounts_with_late_history ?? 0}
            </span>
          </div>
          
          <div className="flex justify-between items-center">
            <span className="text-sm" style={{ color: textMuted }}>Closed accounts</span>
            <span className="font-medium" style={{ color: textPrimary }}>{account_summary?.closed_accounts ?? 0}</span>
          </div>
          
          <div className="flex justify-between items-center">
            <span className="text-sm" style={{ color: textMuted }}>Collections</span>
            <span className="font-medium" style={{ color: account_summary?.collections > 0 ? '#F87171' : textPrimary }}>
              {account_summary?.collections ?? 0}
            </span>
          </div>
          
          <div className="pt-3 mt-3" style={{ borderTop: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)' }}>
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm" style={{ color: textMuted }}>Average account age</span>
              <span className="font-medium text-sm" style={{ color: textPrimary }}>{account_summary?.average_account_age || 'Not available'}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm" style={{ color: textMuted }}>Oldest account age</span>
              <span className="font-medium text-sm" style={{ color: textPrimary }}>{account_summary?.oldest_account_age || 'Not available'}</span>
            </div>
          </div>
        </div>
      </GlassCard>

      {/* Card 2: Overall Credit Usage - Clickable, navigates to Credit Builder Tradelines */}
      <GlassCard theme={theme} clickable={true} onClick={handleUtilizationCardClick}>
        <div className="flex items-center gap-2 mb-4">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(234,178,58,0.2)' }}>
            <TrendingUp size={18} style={{ color: '#FBBF24' }} />
          </div>
          <h3 className="font-semibold" style={{ color: textPrimary }}>Overall Credit Usage</h3>
        </div>
        
        {utilization_summary?.percentage != null ? (
          <div className="flex flex-col items-center">
            <UtilizationRing
              percentage={utilization_summary.percentage}
              color={utilization_summary.color || '#EAB308'}
              size={140}
              strokeWidth={12}
              isDark={isDark}
            />
            
            <div 
              className="mt-4 px-3 py-1 rounded-full text-xs font-semibold"
              style={{ 
                background: `${utilization_summary.color}20`,
                color: utilization_summary.color
              }}
            >
              {utilization_summary.rating}
            </div>
            
            {/* Color Legend - matches ring and track colors */}
            <div className="w-full mt-4 space-y-2">
              {/* Credit Used Row */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span 
                    className="inline-block w-3 h-2 rounded-sm flex-shrink-0"
                    style={{ backgroundColor: utilization_summary.color || '#EAB308' }}
                  />
                  <span className="text-sm" style={{ color: textMuted }}>Credit used</span>
                </div>
                <span className="font-medium" style={{ color: textPrimary }}>
                  {formatCurrency(utilization_summary.credit_card_balance, true)}
                </span>
              </div>
              {/* Credit Limit Row */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span 
                    className="inline-block w-3 h-2 rounded-sm flex-shrink-0"
                    style={{ 
                      backgroundColor: isDark 
                        ? 'rgba(167, 243, 208, 0.35)' 
                        : 'rgba(6, 78, 59, 0.25)' 
                    }}
                  />
                  <span className="text-sm" style={{ color: textMuted }}>Credit limit</span>
                </div>
                <span className="font-medium" style={{ color: textPrimary }}>
                  {formatCurrency(utilization_summary.open_revolving_credit_limit || utilization_summary.total_credit_limit)}
                </span>
              </div>
            </div>
            
            {/* High utilization warning */}
            {utilization_summary.percentage >= 50 && (
              <div className="mt-4 p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
                <div className="flex items-start gap-2">
                  <Info size={16} className="text-yellow-400 flex-shrink-0 mt-0.5" />
                  <p className="text-xs" style={{ color: isDark ? 'rgba(254,240,138,0.8)' : '#854D0E' }}>
                    High revolving utilization may negatively affect credit scores. Lower utilization may help the utilization portion of the profile once updated balances are reported.
                  </p>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <div className="w-12 h-12 rounded-full flex items-center justify-center mb-3" style={{ background: 'rgba(107,114,128,0.2)' }}>
              <XCircle size={24} style={{ color: textMuted }} />
            </div>
            <p className="text-sm font-medium mb-1" style={{ color: textMuted }}>Credit usage not available</p>
            <p className="text-xs" style={{ color: isDark ? '#6B7280' : '#9CA3AF' }}>{utilization_summary?.reason || "No revolving credit limit was found in the imported report."}</p>
          </div>
        )}
      </GlassCard>

      {/* Card 3: Debt Summary */}
      <GlassCard theme={theme}>
        <div className="flex items-center gap-2 mb-4">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(239,68,68,0.2)' }}>
            <DollarSign size={18} style={{ color: '#F87171' }} />
          </div>
          <h3 className="font-semibold" style={{ color: textPrimary }}>Debt Summary</h3>
        </div>
        
        {debt_summary?.has_debt_data ? (
          <div className="space-y-3">
            {/* Total credit limit - not debt, just for reference */}
            <div className="flex justify-between items-center">
              <span className="text-sm" style={{ color: textMuted }}>Total credit limit</span>
              <span className="font-medium" style={{ color: textPrimary }}>
                {debt_summary?.total_credit_limit != null ? formatCurrency(debt_summary.total_credit_limit) : 'Not available'}
              </span>
            </div>
            
            <div className="flex justify-between items-center">
              <span className="text-sm" style={{ color: textMuted }}>Credit card balance</span>
              <span className="font-medium" style={{ color: textPrimary }}>{formatCurrency(debt_summary?.credit_card_balance, debt_summary?.credit_card_balance === 0)}</span>
            </div>
            
            <div className="flex justify-between items-center">
              <span className="text-sm" style={{ color: textMuted }}>Loan debt</span>
              <span className="font-medium" style={{ color: textPrimary }}>{formatCurrency(debt_summary?.loan_debt, debt_summary?.loan_debt === 0)}</span>
            </div>
            
            <div className="flex justify-between items-center">
              <span className="text-sm" style={{ color: textMuted }}>Collections debt</span>
              <span className="font-medium" style={{ color: debt_summary?.collections_debt > 0 ? '#F87171' : textPrimary }}>
                {formatCurrency(debt_summary?.collections_debt, debt_summary?.collections_debt === 0)}
              </span>
            </div>
            
            {/* Self-reported balance - only show if supported */}
            {debt_summary?.self_reported_balance !== null && (
              <div className="flex justify-between items-center">
                <span className="text-sm" style={{ color: textMuted }}>Self-reported balance</span>
                <span className="font-medium" style={{ color: textPrimary }}>{formatCurrency(debt_summary.self_reported_balance)}</span>
              </div>
            )}
            
            <div className="pt-3 mt-3" style={{ borderTop: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)' }}>
              <div className="flex justify-between items-center">
                <span className="font-semibold" style={{ color: textPrimary }}>Total Debt</span>
                <span className="text-xl font-bold" style={{ color: textPrimary }}>{formatCurrency(debt_summary?.total_debt, true)}</span>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <div className="w-12 h-12 rounded-full flex items-center justify-center mb-3" style={{ background: 'rgba(107,114,128,0.2)' }}>
              <XCircle size={24} style={{ color: textMuted }} />
            </div>
            <p className="text-sm" style={{ color: textMuted }}>No debt balances found from imported report.</p>
          </div>
        )}
      </GlassCard>
    </div>
  );
};

export default CreditProfileSnapshot;
